import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { bulkOperationsService } from '../../services/bulkOperationsService';
import { 
  BulkOperationsState, 
  BulkOperationResult, 
  BulkEpicStatusUpdate, 
  BulkFeatureAssignment, 
  BulkAllocationUpdate,
  ImportType 
} from '../../types/bulkOperations';

const initialState: BulkOperationsState = {
  isLoading: false,
  lastResult: null,
  error: null,
};

// Async thunks
export const bulkUpdateEpicStatus = createAsyncThunk(
  'bulkOperations/bulkUpdateEpicStatus',
  async (data: BulkEpicStatusUpdate) => {
    return await bulkOperationsService.bulkUpdateEpicStatus(data);
  }
);

export const bulkAssignFeatures = createAsyncThunk(
  'bulkOperations/bulkAssignFeatures',
  async (data: BulkFeatureAssignment) => {
    return await bulkOperationsService.bulkAssignFeatures(data);
  }
);

export const bulkUpdateAllocations = createAsyncThunk(
  'bulkOperations/bulkUpdateAllocations',
  async (data: BulkAllocationUpdate) => {
    return await bulkOperationsService.bulkUpdateAllocations(data);
  }
);

export const importFromFile = createAsyncThunk(
  'bulkOperations/importFromFile',
  async ({ file, type }: { file: File; type: ImportType }) => {
    return await bulkOperationsService.importFromFile(file, type);
  }
);

export const downloadTemplate = createAsyncThunk(
  'bulkOperations/downloadTemplate',
  async (type: ImportType) => {
    await bulkOperationsService.downloadTemplate(type);
    return type;
  }
);

const bulkOperationsSlice = createSlice({
  name: 'bulkOperations',
  initialState,
  reducers: {
    clearResult: (state) => {
      state.lastResult = null;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Bulk update EPIC status
    builder
      .addCase(bulkUpdateEpicStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(bulkUpdateEpicStatus.fulfilled, (state, action: PayloadAction<BulkOperationResult>) => {
        state.isLoading = false;
        state.lastResult = action.payload;
      })
      .addCase(bulkUpdateEpicStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to update EPIC status';
      });

    // Bulk assign features
    builder
      .addCase(bulkAssignFeatures.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(bulkAssignFeatures.fulfilled, (state, action: PayloadAction<BulkOperationResult>) => {
        state.isLoading = false;
        state.lastResult = action.payload;
      })
      .addCase(bulkAssignFeatures.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to assign features';
      });

    // Bulk update allocations
    builder
      .addCase(bulkUpdateAllocations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(bulkUpdateAllocations.fulfilled, (state, action: PayloadAction<BulkOperationResult>) => {
        state.isLoading = false;
        state.lastResult = action.payload;
      })
      .addCase(bulkUpdateAllocations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to update allocations';
      });

    // Import from file
    builder
      .addCase(importFromFile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(importFromFile.fulfilled, (state, action: PayloadAction<BulkOperationResult>) => {
        state.isLoading = false;
        state.lastResult = action.payload;
      })
      .addCase(importFromFile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to import data';
      });

    // Download template
    builder
      .addCase(downloadTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(downloadTemplate.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(downloadTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to download template';
      });
  },
});

export const { clearResult, clearError } = bulkOperationsSlice.actions;
export default bulkOperationsSlice.reducer;